/**
 * Dual Stream Panel
 *
 * Split view: invoker vs counterparty signal trajectories.
 * Cross-stream inflection timeline.
 */

import { motion, AnimatePresence } from 'framer-motion'
import { useDualStreamAnalysis, useCrossStreamInflections } from '@/hooks/usePragmatic'
import type { StreamAnalysis, CrossStreamInflection } from '@personic/shared'

interface DualStreamPanelProps {
  sessionId: string
  invokerId: string
}

function StreamColumn({
  label,
  icon,
  stream,
  color,
}: {
  label: string
  icon: string
  stream?: StreamAnalysis
  color: string
}) {
  if (!stream) {
    return (
      <div className="p-4 rounded-lg bg-gray-50 text-center">
        <p className="text-sm text-gray-500">No {label.toLowerCase()} data</p>
      </div>
    )
  }

  return (
    <div className={`p-4 rounded-lg border ${color}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">{icon}</span>
        <h4 className="font-semibold text-gray-900 text-sm">{label}</h4>
        <span className="text-xs text-gray-500 ml-auto">
          {stream.turn_count} turn{stream.turn_count !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="space-y-2">
        {stream.has_significant_deviations && (
          <div className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 bg-amber-500 rounded-full" />
            <span className="text-amber-700">Significant deviations detected</span>
          </div>
        )}
        {stream.has_trend_changes && (
          <div className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 bg-blue-500 rounded-full" />
            <span className="text-blue-700">Trend changes detected</span>
          </div>
        )}
        {stream.significant_signals.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {stream.significant_signals.map((signal) => (
              <span
                key={signal}
                className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full"
              >
                {signal}
              </span>
            ))}
          </div>
        )}
        <div className="text-xs text-gray-500 mt-2">
          {stream.events_count} event{stream.events_count !== 1 ? 's' : ''} detected
        </div>
      </div>
    </div>
  )
}

function InflectionTimeline({ inflections }: { inflections: CrossStreamInflection[] }) {
  if (!inflections.length) {
    return (
      <div className="text-center py-4">
        <p className="text-xs text-gray-500">No cross-stream inflections detected</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-700">Cross-Stream Inflections</h4>
      <AnimatePresence>
        {inflections.slice(0, 5).map((inflection, i) => (
          <motion.div
            key={inflection.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
            className="flex items-start gap-3 p-2 rounded bg-gray-50"
          >
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
              inflection.affected_stream === 'invoker'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-amber-100 text-amber-700'
            }`}>
              {inflection.affected_stream === 'invoker' ? 'I' : 'C'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-700">{inflection.description}</p>
              <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                <span>{inflection.signal_name}</span>
                <span>|</span>
                <span>magnitude: {inflection.magnitude.toFixed(2)}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}

export default function DualStreamPanel({ sessionId, invokerId }: DualStreamPanelProps) {
  const dualStream = useDualStreamAnalysis(sessionId)
  const { data: inflectionsData } = useCrossStreamInflections(sessionId)

  const handleAnalyze = () => {
    dualStream.mutate({
      invoker_id: invokerId,
      session_id: sessionId,
      include_cross_stream: true,
    })
  }

  if (!dualStream.data) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-gray-500 mb-3">
          Run dual-stream analysis to see per-party signal breakdowns
        </p>
        <button
          onClick={handleAnalyze}
          disabled={dualStream.isPending}
          className="px-4 py-2 bg-engage-primary text-white rounded-lg text-sm font-medium hover:bg-engage-primary/90 transition-colors disabled:opacity-50"
        >
          {dualStream.isPending ? 'Analyzing...' : 'Run Dual-Stream Analysis'}
        </button>
      </div>
    )
  }

  const result = dualStream.data

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      {/* Split View */}
      <div className="grid grid-cols-2 gap-3">
        <StreamColumn
          label="Invoker"
          icon="🪞"
          stream={result.invoker}
          color="border-blue-200 bg-blue-50/50"
        />
        <StreamColumn
          label="Counterparty"
          icon="🎯"
          stream={result.counterparty}
          color="border-amber-200 bg-amber-50/50"
        />
      </div>

      {/* Cross-Stream Inflections */}
      {inflectionsData && (
        <InflectionTimeline inflections={inflectionsData.inflections} />
      )}

      {/* Re-analyze button */}
      <div className="text-center">
        <button
          onClick={handleAnalyze}
          disabled={dualStream.isPending}
          className="text-xs text-engage-primary hover:text-engage-primary/80"
        >
          {dualStream.isPending ? 'Refreshing...' : 'Refresh analysis'}
        </button>
      </div>
    </motion.div>
  )
}
